// Pieces UI: badge, page dots, swipe container, setup dialog, Screen 2, summary.
import { useEffect, useRef, useState } from 'react';
import { ThumbsUp, Square, Timer, Ruler, ClipboardList, Target, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DISTANCE_PRESETS, pieceName } from '@/lib/pieces';
import { formatSplitTenths } from '@/lib/strokeSplit';
import { paceState } from '@/components/CockpitDisplay';
import { PRE_LOADED_PROGRAMS } from '@/pages/TrainingPrograms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const PIECE_BLUE = 'text-[#1565C0] dark:text-[#4A9EEB]';
const PIECE_BG = 'bg-[#1565C0] dark:bg-[#4A9EEB]';
const mono = { fontFamily: 'JetBrains Mono, ui-monospace, monospace' };

export const fmtClock = (s) => {
  const t = Math.max(0, Math.round(s || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const ss = String(t % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};
const fmtDist = (m) => `${Math.round(m || 0)} m`;

export function ScreenBadge({ screen, label }) {
  const isPiece = screen === 2;
  return (
    <div
      className={`absolute top-2 left-2 z-30 flex items-center gap-2 rounded-xl px-2.5 py-1.5 shadow-sm backdrop-blur-md ${
        isPiece ? `${PIECE_BG} text-white` : 'bg-zinc-200/90 dark:bg-zinc-700/90 text-zinc-700 dark:text-zinc-100'
      }`}
      data-testid={`screen-badge-${screen}`}
    >
      <span className="text-2xl font-black leading-none" style={mono}>{screen}</span>
      <span className="text-xs font-bold uppercase tracking-widest leading-none truncate max-w-[52vw] sm:max-w-[28rem]">{label}</span>
    </div>
  );
}

export function PageDots({ screen, onSelect }) {
  return (
    <div className="flex justify-center gap-2 py-2" data-testid="screen-page-dots">
      {[1, 2].map((n) => (
        <button
          key={n}
          type="button"
          onClick={(e) => { e.stopPropagation(); onSelect(n); }}
          aria-label={`Screen ${n}`}
          data-testid={`screen-dot-${n}`}
          className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${
            screen === n ? `w-7 ${n === 2 ? PIECE_BG : 'bg-zinc-500'}` : 'w-2.5 bg-zinc-300 dark:bg-zinc-600'
          }`}
        />
      ))}
    </div>
  );
}

// Two live screens side by side; horizontal swipe moves between them.
export function SwipeScreens({ enabled, screen, onScreen, children }) {
  const start = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!start.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.current.x;
    const dy = t.clientY - start.current.y;
    start.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) onScreen(dx < 0 ? 2 : 1);
  };
  // Structure stays the same whether or not a piece exists, so Screen 1
  // (the cockpit) never remounts when Screen 2 appears or disappears.
  return (
    <div data-testid={enabled ? 'swipe-screens' : undefined}>
      {/* No transform here: a transformed ancestor would trap the cockpit's
          position:fixed fullscreen mode inside this box (metrics vanished). */}
      <div className={enabled ? 'overflow-hidden' : ''} onTouchStart={enabled ? onTouchStart : undefined} onTouchEnd={enabled ? onTouchEnd : undefined}>
        <div
          className={`flex items-stretch transition-[margin] duration-300 ease-out ${enabled ? 'w-[200%]' : 'w-full'}`}
          style={enabled ? { marginLeft: screen === 2 ? '-100%' : '0' } : undefined}
        >
          <div className={`${enabled ? 'w-1/2' : 'w-full'} relative ${enabled && screen === 2 ? '[&_.cockpit-fullscreen]:hidden' : ''}`} aria-hidden={enabled && screen !== 1} data-testid="screen-1">{children[0]}</div>
          {enabled && <div className="w-1/2 relative" aria-hidden={screen !== 2} data-testid="screen-2">{children[1]}</div>}
        </div>
      </div>
      {enabled && <PageDots screen={screen} onSelect={onScreen} />}
    </div>
  );
}

const TYPES = [
  { id: 'distance', label: 'Distance', icon: Ruler },
  { id: 'time', label: 'Time', icon: Timer },
  { id: 'program', label: 'Program', icon: ClipboardList },
  { id: 'target', label: 'Target', icon: Target },
];
const chip = (on) => `px-3 py-2 rounded-full text-sm font-bold transition-colors ${
  on ? `${PIECE_BG} text-white` : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
}`;
const parseMmSs = (v) => {
  const [m, s] = String(v).split(':');
  const secs = (parseInt(m, 10) || 0) * 60 + (parseInt(s, 10) || 0);
  return secs > 0 ? secs : 0;
};

function DistancePicker({ value, onChange, prefix }) {
  const isPreset = DISTANCE_PRESETS.includes(value);
  return (
    <div className="flex flex-wrap gap-2 items-center">
      {DISTANCE_PRESETS.map((d) => (
        <button key={d} type="button" className={chip(value === d)} onClick={() => onChange(d)} data-testid={`${prefix}-preset-${d}`}>
          {d >= 1000 ? `${d / 1000} km` : `${d} m`}
        </button>
      ))}
      <input
        type="number"
        min="50"
        inputMode="numeric"
        placeholder="Custom m"
        value={isPreset ? '' : value || ''}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        className="w-28 px-3 py-2 rounded-full border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm"
        data-testid={`${prefix}-custom-input`}
      />
    </div>
  );
}

export function PieceSetupDialog({ open, onOpenChange, onStart }) {
  const [type, setType] = useState('distance');
  const [dist, setDist] = useState(500);
  const [time, setTime] = useState('10:00');
  const [pace, setPace] = useState('2:05');
  const [programs, setPrograms] = useState(PRE_LOADED_PROGRAMS);
  const [programIdx, setProgramIdx] = useState(0);
  const [mode, setMode] = useState('now');

  useEffect(() => {
    if (!open) return;
    setMode('now');
    fetch(`${API}/programs`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : JSON.parse(localStorage.getItem('custom_programs') || '[]')))
      .catch(() => JSON.parse(localStorage.getItem('custom_programs') || '[]'))
      .then((custom) => setPrograms([...(Array.isArray(custom) ? custom : []), ...PRE_LOADED_PROGRAMS]));
  }, [open]);

  const def = (() => {
    if (type === 'distance') return dist >= 50 ? { type, value: dist } : null;
    if (type === 'time') { const v = parseMmSs(time); return v >= 10 ? { type, value: v } : null; }
    if (type === 'target') { const p = parseMmSs(pace); return dist >= 50 && p >= 60 ? { type, value: dist, targetPace: p } : null; }
    const pr = programs[programIdx];
    if (!pr?.intervals?.length) return null;
    const { icon, ...rest } = pr; // icons are components — keep the def serialisable
    return { type, program: { ...rest, intervals: pr.intervals } };
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 border border-zinc-200 dark:border-zinc-700" data-testid="piece-setup-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className={PIECE_BLUE}>Start a piece</DialogTitle>
          <DialogDescription className="text-zinc-600 dark:text-zinc-300">The session keeps recording the whole time. Swipe to switch screens.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl p-1" data-testid="piece-type-group">
          {TYPES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setType(id)}
              data-testid={`piece-type-${id}`}
              className={`flex flex-col items-center gap-1 py-2 rounded-lg text-xs font-bold transition-colors ${
                type === id ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-white' : 'text-zinc-500'
              }`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        <div className="min-h-[120px] py-2 space-y-3">
          {(type === 'distance' || type === 'target') && <DistancePicker value={dist} onChange={setDist} prefix={`piece-${type}`} />}
          {type === 'time' && (
            <label className="flex items-center gap-3 text-sm font-medium">
              Duration (mm:ss)
              <input value={time} onChange={(e) => setTime(e.target.value)} className="w-28 px-3 py-2 rounded-full border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm" style={mono} data-testid="piece-time-input" />
            </label>
          )}
          {type === 'target' && (
            <label className="flex items-center gap-3 text-sm font-medium">
              Target pace /500m
              <input value={pace} onChange={(e) => setPace(e.target.value)} className="w-28 px-3 py-2 rounded-full border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 text-sm" style={mono} data-testid="piece-target-pace-input" />
            </label>
          )}
          {type === 'program' && (
            <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1" data-testid="piece-program-list">
              {programs.map((p, i) => (
                <button
                  key={p.program_id || p.id || i}
                  type="button"
                  onClick={() => setProgramIdx(i)}
                  data-testid={`piece-program-${i}`}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    programIdx === i ? 'border-[#1565C0] dark:border-[#4A9EEB] bg-blue-50 dark:bg-blue-950/30' : 'border-zinc-200 dark:border-zinc-700'
                  }`}
                >
                  <div className="font-bold">{p.name}</div>
                  <div className="text-xs text-zinc-500">{(p.intervals || []).length} intervals</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl p-1" data-testid="piece-start-mode-group">
          {[{ id: 'now', label: 'Start now' }, { id: 'countdown', label: '10-second countdown' }].map((o) => (
            <button key={o.id} type="button" onClick={() => setMode(o.id)} data-testid={`piece-start-mode-${o.id}`}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${mode === o.id ? 'bg-white dark:bg-zinc-700 shadow-sm' : 'text-zinc-500'}`}>
              {o.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={!def}
          onClick={() => { onStart(def, mode); onOpenChange(false); }}
          className={`w-full py-3 rounded-full ${PIECE_BG} text-white font-bold uppercase tracking-widest transition-opacity disabled:opacity-40 hover:opacity-90`}
          data-testid="piece-start-button"
        >
          {def ? `Start ${pieceName(def)}` : 'Choose a piece'}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid, big }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{label}</span>
      <span className={`${big ? 'text-3xl sm:text-4xl' : 'text-xl sm:text-2xl'} font-bold text-zinc-900 dark:text-white`} style={mono} data-testid={testid}>{value}</span>
    </div>
  );
}

export function PieceSummaryCard({ result, onDismiss }) {
  return (
    <button type="button" onClick={onDismiss} className="w-full h-full flex flex-col items-center justify-center gap-4 p-6 text-left" data-testid="piece-summary-card">
      <div className={`text-sm font-black uppercase tracking-[0.2em] ${PIECE_BLUE}`}>Piece {result.piece_number} complete · {result.piece_name}</div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 w-full max-w-2xl">
        <Stat label="Distance" value={fmtDist(result.distance_m)} testid="piece-summary-distance" />
        <Stat label="Time" value={fmtClock(result.time_s)} testid="piece-summary-time" />
        <Stat label="Avg split" value={formatSplitTenths(result.avg_split_s)} testid="piece-summary-avg-split" />
        <Stat label="Avg rate" value={result.avg_stroke_rate ?? '—'} testid="piece-summary-avg-rate" />
        <Stat label="Strokes" value={result.stroke_count ?? '—'} testid="piece-summary-strokes" />
      </div>
      <span className="text-xs text-zinc-400">Tap to return to the session</span>
    </button>
  );
}

function TargetCue({ currentSplit, targetPace }) {
  if (!(currentSplit > 0)) return null;
  const st = paceState(currentSplit - targetPace);
  return (
    <div className="flex items-center gap-2" data-testid="piece-target-cue" style={{ color: st.color }}>
      {st.dir === '▲'
        ? <ThumbsUp className="w-9 h-9" fill="#21B86A" stroke="#000" strokeWidth={2.25} style={{ filter: 'drop-shadow(0 0 8px #21B86A)' }} />
        : <span className="text-4xl leading-none">{st.dir}</span>}
      <span className="text-xs font-bold tracking-widest">{st.word}</span>
    </div>
  );
}

export function PieceScreen({ piece, progress, countdown, summary, currentSplit, strokeRate, onEnd, onDismissSummary, onCancelCountdown }) {
  const name = piece?.name || (countdown ? pieceName(countdown.def) : summary?.piece_name) || '';
  let body;
  if (summary && !piece) {
    body = <PieceSummaryCard result={summary} onDismiss={onDismissSummary} />;
  } else if (countdown) {
    body = (
      <div className="flex flex-col items-center justify-center gap-3 h-full" data-testid="piece-countdown">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">Starts in</span>
        <span className={`text-8xl font-black ${PIECE_BLUE}`} style={mono} data-testid="piece-countdown-value">{countdown.left}</span>
        <button type="button" onClick={onCancelCountdown} className="flex items-center gap-1 text-xs font-bold text-zinc-500 hover:text-red-600" data-testid="piece-countdown-cancel">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    );
  } else if (piece && progress) {
    const { def } = piece;
    const iv = progress.interval;
    const remaining = def.type === 'time'
      ? fmtClock(def.value - progress.time)
      : def.type === 'program'
        ? (iv ? (iv.unit === 'm' ? fmtDist(iv.remaining) : fmtClock(iv.remaining)) : '—')
        : fmtDist(def.value - progress.distance);
    const avgSplit = progress.distance > 0 ? progress.time / (progress.distance / 500) : 0;
    body = (
      <div className="flex flex-col h-full gap-4 pt-12 pb-2 px-3">
        <div className="flex items-end justify-center gap-6 flex-wrap">
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              {def.type === 'program' && iv ? `${iv.type === 'rest' ? 'Rest' : 'Work'} ${iv.index + 1}/${iv.count} · remaining` : 'Remaining'}
            </span>
            <span className="text-6xl sm:text-7xl font-black text-zinc-900 dark:text-white" style={mono} data-testid="piece-remaining">{remaining}</span>
          </div>
          {def.type === 'target' && <TargetCue currentSplit={currentSplit} targetPace={def.targetPace} />}
        </div>
        {def.type === 'program' && iv && (iv.targetPace || iv.targetSPM) && (
          <div className="text-center text-sm font-bold text-zinc-500" data-testid="piece-interval-target">
            Target {iv.targetPace ? `${formatSplitTenths(iv.targetPace)}/500m` : ''}{iv.targetSPM ? ` @ ${iv.targetSPM} spm` : ''}
          </div>
        )}
        <div className="h-2 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden" data-testid="piece-progress-bar">
          <div className={`h-full ${PIECE_BG} transition-[width] duration-700`} style={{ width: `${Math.round(progress.fraction * 100)}%` }} />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          <Stat label="Piece time" value={fmtClock(progress.time)} testid="piece-time" />
          <Stat label="Piece dist" value={fmtDist(progress.distance)} testid="piece-distance" />
          <Stat label="Split" value={formatSplitTenths(currentSplit)} testid="piece-split" />
          <Stat label="Rate" value={strokeRate || 0} testid="piece-rate" />
          <Stat label="Avg split" value={formatSplitTenths(avgSplit)} testid="piece-avg-split" />
        </div>
        <div className="flex justify-center mt-auto">
          <button type="button" onClick={(e) => { e.stopPropagation(); onEnd(); }}
            className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#1565C0] dark:border-[#4A9EEB] text-sm font-bold uppercase tracking-widest text-[#1565C0] dark:text-[#4A9EEB] hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
            data-testid="piece-end-button">
            <Square className="w-4 h-4" /> End piece
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="relative h-full min-h-[320px] rounded-2xl border-2 border-[#1565C0]/40 dark:border-[#4A9EEB]/40 bg-white dark:bg-zinc-900" data-testid="piece-screen">
      <ScreenBadge screen={2} label={`Piece · ${name}`} />
      {body}
    </div>
  );
}
